import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";

import { CourseCard } from "~/components/courses/CourseCard";
import PricingTiers from "~/components/pricing/PricingTiers";
import CtaBanner from "~/components/sections/CtaBanner";
import Faq from "~/components/sections/Faq";
import ProcessSteps from "~/components/sections/ProcessSteps";
import TechMarquee from "~/components/sections/TechMarquee";
import Testimonial from "~/components/sections/Testimonial";
import WhyChoose from "~/components/sections/WhyChoose";
import { COURSES } from "~/data/courses";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import generateSitemapEntriesFor from "~/helpers/sitemapEntriesWithLocal";
import common from "~/i18n/common";
import faqDict from "~/i18n/faq";
import pages from "~/i18n/pages";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";
import { faqSchema } from "~/seo/schema";

import AiSpotlight from "./AiSpotlight";
import Hero from "./Hero";
import "./home.scss";

export const meta: MetaFunction = ({ params }) => {
  const lang = langFromParams(params);
  const t = pages[lang].home;

  return [
    ...buildMeta({
      lang,
      path: "/",
      title: t.metaTitle,
      description: t.metaDescription,
    }),
    { "script:ld+json": faqSchema(faqDict[lang].items) },
  ];
};

export const handle: SEOHandle = generateSitemapEntriesFor(
  "/",
  undefined,
  "weekly",
  1.0,
);

export default function HomePage() {
  const t = useT(pages);
  const c = useT(common);
  const localize = useLocalizedPath();

  return (
    <main id="main-content">
      <Hero />
      <TechMarquee />
      <ProcessSteps variant="split" />
      <WhyChoose />
      <AiSpotlight />

      <section className="ss-section ss-container">
        <div className="ss-heading ss-heading--center">
          <div>
            <p className="ss-eyebrow">{t.home.skills.eyebrow}</p>
            <h2 className="ss-h2 ss-heading__title">{t.home.skills.title}</h2>
          </div>
        </div>
        <div className="ss-grid-3">
          {COURSES.map((course) => (
            <CourseCard key={course.slug} course={course} />
          ))}
        </div>
      </section>

      <section className="ss-section ss-container">
        <div className="ss-heading ss-heading--center">
          <div>
            <p className="ss-eyebrow">{t.home.pricing.eyebrow}</p>
            <h2 className="ss-h2 ss-heading__title">{t.home.pricing.title}</h2>
          </div>
        </div>

        <PricingTiers />

        <p className="ss-home__compare">
          <Link to={localize("/pricing")} className="ss-link-mono">
            {c.cta.comparePlans}
            <span className="ss-flip" aria-hidden="true">
              →
            </span>
          </Link>
        </p>
      </section>

      <Testimonial />
      <Faq />

      <CtaBanner
        title={t.home.cta.title}
        body={t.home.cta.body}
        action={c.cta.scheduleConsultation}
      />
    </main>
  );
}
